import { apiUrl } from '../utils/api-url';

/**
 * Summary row returned by `GET /api/customers` — derived from project counts and
 * augmented with the customer doc's display name when a CustomerDocument exists.
 * Backward-compatible with the original {id, projectCount} shape from #143.
 */
export interface CustomerSummary {
  id: string;
  projectCount: number;
  displayName: string;
  legalName?: string;
  isPublicSector?: boolean;
}

export interface BrandingInfo {
  logoBase64?: string;
  logoUrl?: string;
  primaryColour?: string;
  secondaryColour?: string;
  accentColour?: string;
  textColour?: string;
}

/** Full Cosmos `customers` container record. */
export interface CustomerDocument {
  id: string;
  legalName: string;
  displayName: string;
  country?: string;
  sector?: string;
  isPublicSector: boolean;
  regulatoryProfile?: string;
  address?: string;
  branding: BrandingInfo;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerPatch {
  legalName?: string;
  displayName?: string;
  country?: string;
  sector?: string;
  isPublicSector?: boolean;
  regulatoryProfile?: string;
  address?: string;
  branding?: Partial<BrandingInfo>;
}

export interface ImportResult {
  customer: CustomerDocument;
  patchedProjectId?: string;
  overrideKeysSet: string[];
  logoEmbedded: boolean;
}

export async function listCustomers(token: string): Promise<CustomerSummary[]> {
  const res = await fetch(apiUrl('/api/customers'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to list customers: ${res.status}`);
  return res.json();
}

export async function getCustomer(id: string, token: string): Promise<CustomerDocument | null> {
  const res = await fetch(apiUrl(`/api/customers/${encodeURIComponent(id)}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to get customer ${id}: ${res.status}`);
  return res.json();
}

export async function patchCustomer(
  id: string,
  patch: CustomerPatch,
  token: string,
): Promise<CustomerDocument> {
  const res = await fetch(apiUrl(`/api/customers/${encodeURIComponent(id)}`), {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to patch customer ${id}: ${res.status}`);
  return res.json();
}

/**
 * Trigger the admin importer. Reads `projects/<customerId>/variables.json` from the
 * backend's working tree, upserts the CustomerDocument with branding, and patches the
 * active project's customerOverrides with the dotted-key flattened view.
 */
export async function importCustomer(customerId: string, token: string): Promise<ImportResult> {
  const res = await fetch(
    apiUrl(`/api/admin/import-customer/${encodeURIComponent(customerId)}`),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to import customer ${customerId}: ${res.status} — ${body}`);
  }
  return res.json();
}
